import { type Dispatch, type SetStateAction, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDraggable } from "@dnd-kit/core";
import type { RugbyEventMatchResultSummary, MatchPublic, MatchTeamPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { SCHOOL_COLORS, fmtTime } from "@/lib/scheduler-utils";
import { MatchClock } from "@/components/match-clock";
import { MatchScoreboard } from "@/components/match-scoreboard";
import { X, Flag, Activity, Copy, ChevronsDown } from "lucide-react";

/** Match name convention: "Home vs Away". Either side can be "TBD". */
export function matchNeedsOpponent(m: MatchPublic): boolean {
  const parts = m.name.split(" vs ");
  return parts[0] === "TBD" || parts[1] === "TBD";
}

interface MatchCardProps {
  match: MatchPublic;
  teams: MatchTeamPublic[];
  result?: RugbyEventMatchResultSummary;
  flash?: { type: string; side: string; points: number; at: number };
  teamColorMap: Map<string, number>;
  teamSchoolMap: Map<string, string>;
  canManage: boolean;
  /** 191 -- this match overlaps another on the same field. Red framing only;
   *  editing is never blocked (the publish action carries the warning). */
  isConflicting?: boolean;
  /** 193 -- grid multi-select: while active, clicking the card toggles
   *  selection instead of the inner edit affordances. */
  selectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  /** 193 -- "running late" quick action: opens the bulk-shift dialog anchored
   *  on this match. */
  onShiftFromHere?: (match: MatchPublic) => void;
  /** 129 # 128 -- when exactly one non-host EventSchool exists, the parent
   *  supplies this callback. MatchCard renders a "+ 1-2-1" button on the
   *  TBD away pill to mirror the host team onto the counterparty in one
   *  click. Undefined when the affordance isn't applicable. */
  onQuickPairCounterparty?: (match: MatchPublic) => void;
  counterpartyName?: string;
  setMatches: Dispatch<SetStateAction<MatchPublic[]>>;
  setMatchTeams: Dispatch<SetStateAction<Record<string, MatchTeamPublic[]>>>;
  refreshMatches: () => Promise<void>;
  onResizeStart?: (e: React.MouseEvent, matchId: string, totalMins: number) => void;
  resizeDeltaMins?: number;
  pxPerMin: number;
  gridStartMs: number;
  slotHeight: number;
}

export function MatchCard({
  match: m, teams: mt, result, flash, teamColorMap, teamSchoolMap, canManage,
  isConflicting = false,
  selectMode = false, isSelected = false, onToggleSelect, onShiftFromHere,
  onQuickPairCounterparty, counterpartyName,
  setMatches, setMatchTeams, refreshMatches,
  onResizeStart, resizeDeltaMins = 0, pxPerMin, gridStartMs, slotHeight,
}: MatchCardProps) {
  const [leftName, rightName] = m.name.split(" vs ");
  const homeTeam = mt.find((t) => t.role === "home");
  const awayTeam = mt.find((t) => t.role === "away");
  const needsOpp = matchNeedsOpponent(m);

  const homeName = homeTeam?.team_name ?? leftName;
  const awayName = awayTeam?.team_name ?? rightName ?? "TBD";

  const hIdx = homeTeam ? (teamColorMap.get(homeTeam.team_id) ?? 0) : (teamColorMap.get(leftName) ?? 0);
  const aIdx = awayTeam ? (teamColorMap.get(awayTeam.team_id) ?? 1) : (teamColorMap.get(rightName) ?? 1);
  const hc = SCHOOL_COLORS[hIdx % SCHOOL_COLORS.length];
  const ac = SCHOOL_COLORS[aIdx % SCHOOL_COLORS.length];

  const mStartMs = new Date(m.starts_at).getTime();
  const offsetMin = Math.max(0, (mStartMs - gridStartMs) / 60_000);
  const baseDuration = m.first_half_mins + m.break_mins + m.second_half_mins;
  const durationMin = Math.max(15, baseDuration + resizeDeltaMins);
  const topPx = offsetMin * pxPerMin;
  const heightPx = Math.max(slotHeight * 0.8, durationMin * pxPerMin);

  // Live preview of the half length while resizing -- mirrors the snap math in
  // schedule-grid.tsx so the label and the commit agree.
  const isResizing = resizeDeltaMins !== 0;
  const previewPlayMins = Math.max(2, Math.round((durationMin - m.break_mins) / 2) * 2);
  const previewHalf = isResizing ? previewPlayMins / 2 : Math.max(m.first_half_mins, m.second_half_mins);
  const previewEndsAt = isResizing
    ? new Date(mStartMs + (previewPlayMins + m.break_mins) * 60_000).toISOString()
    : m.ends_at;

  // 184: the card is a dnd-kit draggable -- reschedule by dragging it to another
  // grid cell (works on touch via the shared Touch sensor: long-press to lift).
  // Drop routing lives in the scheduler's central onDragEnd; while dragging, the
  // card just dims and a label follows the pointer via the DragOverlay.
  const drag = useDraggable({
    id: `match-move-${m.id}`,
    data: { kind: "match-move", matchId: m.id, label: m.name },
    disabled: !canManage,
  });

  async function clearTeam(role: "home" | "away") {
    const existing = mt.find((t) => t.role === role);
    const newName = role === "home" ? `TBD vs ${rightName}` : `${leftName} vs TBD`;
    setMatches((prev) => prev.map((x) => x.id === m.id ? { ...x, name: newName } : x));
    if (existing) {
      setMatchTeams((prev) => ({
        ...prev,
        [m.id]: (prev[m.id] ?? []).filter((t) => t.role !== role),
      }));
    }
    notifySaving();
    try {
      if (existing) await api.removeMatchTeam(m.id, existing.team_id);
      await api.updateMatch(m.id, { name: newName });
      notifySaved();
    } catch { refreshMatches(); }
  }

  async function deleteMatch() {
    setMatches((prev) => prev.filter((x) => x.id !== m.id));
    setMatchTeams((prev) => { const next = { ...prev }; delete next[m.id]; return next; });
    try { await api.deleteMatch(m.id); } catch { refreshMatches(); }
  }

  return (
    <div
      data-item="match" data-item-id={m.id}
      data-conflict={isConflicting ? "true" : undefined}
      data-selected={isSelected ? "true" : undefined}
      title={isConflicting ? "Overlaps another match on this field" : undefined}
      // 193 -- capture-phase so select-mode clicks toggle selection before any
      // inner edit affordance fires. Drag still works (sensors need movement
      // before activating, so a plain click never starts a drag).
      onClickCapture={selectMode && onToggleSelect ? (e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect(); } : undefined}
      className={`group absolute left-1 right-1 z-[5] overflow-hidden rounded border bg-background shadow-sm ${needsOpp ? "border-dashed" : ""} ${drag.isDragging ? "opacity-40" : ""} ${isConflicting && !isSelected ? "border-destructive ring-2 ring-destructive/60" : ""} ${isSelected ? "border-primary ring-2 ring-primary" : ""} ${selectMode ? "cursor-pointer" : ""}`}
      style={{ top: topPx, height: heightPx }}
    >
      <div
        ref={drag.setNodeRef}
        {...(canManage ? drag.listeners : {})}
        {...(canManage ? drag.attributes : {})}
        className={`flex h-full flex-col p-1.5 ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <TeamPill name={homeName} isTBD={leftName === "TBD"} color={hc}
              brand={brandFromTeam(homeTeam)}
              schoolName={homeTeam ? teamSchoolMap.get(homeTeam.team_id) : teamSchoolMap.get(leftName)}
              canClear={canManage && leftName !== "TBD"} onClear={() => clearTeam("home")} clearAction="clear-home" />
            <span className="text-muted-foreground">vs</span>
            <TeamPill name={awayName} isTBD={rightName === "TBD"} color={ac}
              brand={brandFromTeam(awayTeam)}
              schoolName={awayTeam ? teamSchoolMap.get(awayTeam.team_id) : teamSchoolMap.get(rightName)}
              canClear={canManage && rightName !== "TBD"} onClear={() => clearTeam("away")} clearAction="clear-away"
              onQuickPair={
                canManage && rightName === "TBD" && leftName !== "TBD" && onQuickPairCounterparty
                  ? () => onQuickPairCounterparty(m)
                  : undefined
              }
              quickPairTooltip={counterpartyName ? `Create matching team for ${counterpartyName}` : undefined} />
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {m._actions.includes("score_live") && !result && (
              <Link
                to={m.sport_id === "NET"
                  ? `/netball/matches/${m.id}/score`
                  : m.sport_id === "RUG7" || m.sport_id === "RUG10"
                    ? `/rugby-sevens/matches/${m.id}/score`
                    : `/rugby/matches/${m.id}/score`}
                data-action="score-live"
                title="Score live"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="flex shrink-0 items-center gap-1 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
              >
                <Activity className="h-3 w-3" aria-hidden />
                Score live
              </Link>
            )}
            {canManage && onShiftFromHere && !selectMode && (
              <button type="button" title="Running late? Shift this + later matches" data-action="shift-from-here"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onShiftFromHere(m); }}
                className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-primary group-hover:opacity-100">
                <ChevronsDown className="h-3.5 w-3.5" />
              </button>
            )}
            {canManage && (
              <button type="button" title="Delete match" data-action="delete-match"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); deleteMatch(); }}
                className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {result && <LiveScoreStrip matchId={m.id} sportId={m.sport_id} result={result} flash={flash} startsAt={m.starts_at} endsAt={m.ends_at} />}
        {(() => {
          // Compact clock on the card -- only while the match is in flight
          // (±5min) or finalised, otherwise matches far in the future clutter.
          const now = Date.now();
          const startsMs = new Date(m.starts_at).getTime();
          const endsMs = new Date(m.ends_at).getTime();
          const inFlight = now >= startsMs - 5 * 60_000 && now <= endsMs + 30 * 60_000;
          if (!inFlight && !result?.final) return null;
          return (
            <div className="mt-0.5" data-field="match-clock-compact">
              <MatchClock
                startsAt={m.starts_at}
                firstHalfMins={m.first_half_mins}
                breakMins={m.break_mins}
                secondHalfMins={m.second_half_mins}
                final={result?.final ?? false}
                compact
              />
            </div>
          );
        })()}
        <RefereeInline matchId={m.id} value={m.referee} canEdit={canManage} setMatches={setMatches} refreshMatches={refreshMatches} />
        <HalfTimesInline
          matchId={m.id}
          startsAt={m.starts_at}
          endsAt={previewEndsAt}
          firstHalf={previewHalf}
          breakMins={m.break_mins}
          secondHalf={previewHalf}
          canEdit={canManage && !isResizing}
          isResizing={isResizing}
          setMatches={setMatches}
          refreshMatches={refreshMatches}
        />
      </div>
      {canManage && onResizeStart && (
        <div
          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize bg-border/30 hover:bg-border/60"
          onMouseDown={(e) => onResizeStart(e, m.id, baseDuration)}
        />
      )}
    </div>
  );
}

// ─── Live Score Strip ──────────────────────────────────────────
// Shows score + live/final badge. Clickable → full scoring screen.
function LiveScoreStrip({ matchId, sportId, result, flash, startsAt, endsAt }: {
  matchId: string;
  sportId: string | null;
  result: RugbyEventMatchResultSummary;
  flash?: { type: string; side: string; points: number; at: number };
  startsAt: string;
  endsAt: string;
}) {
  const [nowTick, setNowTick] = useState(Date.now());
  // Re-render ~every second while a flash is fading so it disappears on time.
  useEffect(() => {
    if (!flash) return;
    const stale = Date.now() - flash.at > 4_000;
    if (stale) return;
    const t = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(t);
  }, [flash?.at]);

  const now = nowTick;
  const startsMs = new Date(startsAt).getTime();
  const endsMs = new Date(endsAt).getTime();
  const hasStarted = now >= startsMs;
  // 5-min grace past scheduled end, then we drop LIVE even without Finalise.
  const isLive = !result.final && hasStarted && now <= endsMs + 5 * 60_000;
  const pastEnd = !result.final && hasStarted && now > endsMs + 5 * 60_000;
  const showNothing = !hasStarted && result.home_score === 0 && result.away_score === 0 && !result.final;
  if (showNothing) return null;

  const label = result.final ? "FINAL" : isLive ? "LIVE" : pastEnd ? "FT" : "SCORE";
  const badgeClass = isLive
    ? "bg-red-500 text-white"
    : "bg-muted text-muted-foreground";

  const flashVisible = flash && (now - flash.at) < 4_000;
  const flashSide = flashVisible
    ? (flash.side === "home" ? "home" : flash.side === "away" ? "away" : null)
    : null;

  // 098: per-sport scoring routes. Default to rugby when sport is unset
  // (rugby is the seed default and the only sport with result aggregation
  // in EventResultsResponse today).
  const scoreUrl = sportId === "NET"
    ? `/netball/matches/${matchId}/score`
    : sportId === "RUG7" || sportId === "RUG10"
      ? `/rugby-sevens/matches/${matchId}/score`
      : `/rugby/matches/${matchId}/score`;

  return (
    <Link
      to={scoreUrl}
      data-action="open-score"
      onClick={(e) => e.stopPropagation()}
      className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold hover:opacity-80"
      title="Open scoring"
    >
      <span className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] uppercase leading-none ${badgeClass}`}>
        {isLive && !result.final && (
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden />
        )}
        {label}
      </span>
      <MatchScoreboard
        home={result.home_team}
        away={result.away_team}
        homeScore={result.home_score}
        awayScore={result.away_score}
        compact
        flashSide={flashSide}
        flashEventType={flashVisible ? flash?.type ?? null : null}
      />
    </Link>
  );
}

// ─── Team Pill ──────────────────────────────────────────────────

type TeamBrand = {
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
};

function brandFromTeam(t: MatchTeamPublic | undefined): TeamBrand | null {
  if (!t) return null;
  if (!t.school_primary_color || !t.school_secondary_color) return null;
  return {
    logoUrl: t.school_logo_url,
    primaryColor: t.school_primary_color,
    secondaryColor: t.school_secondary_color,
  };
}

function TeamPill({ name, isTBD, color, brand, schoolName, canClear, onClear, clearAction, onQuickPair, quickPairTooltip }: {
  name: string;
  isTBD: boolean;
  color: { bg: string; text: string };
  brand?: TeamBrand | null;
  schoolName?: string;
  canClear: boolean;
  onClear: () => void;
  clearAction?: string;
  onQuickPair?: () => void;
  quickPairTooltip?: string;
}) {
  if (isTBD) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-muted-foreground italic">drop opponent</span>
        {onQuickPair && (
          <button
            type="button"
            title={quickPairTooltip ?? "Create 1-2-1 team"}
            data-action="quick-pair-counterparty"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onQuickPair(); }}
            className="inline-flex items-center gap-0.5 rounded border border-primary/30 bg-primary/5 px-1 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
          >
            <Copy className="h-3 w-3" aria-hidden />
            1-2-1
          </button>
        )}
      </span>
    );
  }
  // Ad-hoc teams have their school name baked into `name` (see teamMatchLabel
  // in schedule-grid.tsx) -- don't render it a second time in parens.
  const showSchool = schoolName && !name.startsWith(`${schoolName} `);
  const branded = !!brand;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium ${branded ? "" : `${color.bg} ${color.text}`}`}
      style={branded ? { backgroundColor: brand!.primaryColor, color: brand!.secondaryColor } : undefined}
      data-branded={branded ? "true" : "false"}
    >
      {brand?.logoUrl && (
        <img src={brand.logoUrl} alt="" className="h-3.5 w-3.5 object-contain" />
      )}
      {name}
      {showSchool && <span className="font-normal opacity-60">({schoolName})</span>}
      {canClear && (
        <button type="button" title="Clear team" data-action={clearAction} onClick={(e) => { e.stopPropagation(); onClear(); }}
          className="-mr-0.5 ml-0.5 rounded-full opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100">
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// ─── Half Times Inline Edit ────────────────────────────────────

function hhmmFromIso(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function HalfTimesInline({ matchId, startsAt, endsAt, firstHalf, breakMins, secondHalf, canEdit, isResizing = false, setMatches, refreshMatches }: {
  matchId: string;
  startsAt: string;
  endsAt: string;
  firstHalf: number;
  breakMins: number;
  secondHalf: number;
  canEdit: boolean;
  isResizing?: boolean;
  setMatches: Dispatch<SetStateAction<MatchPublic[]>>;
  refreshMatches: () => Promise<void>;
}) {
  // Halves are always equal -- display the larger value so legacy uneven data rounds up
  const displayHalf = Math.max(firstHalf, secondHalf);
  const [editing, setEditing] = useState(false);
  const [localStart, setLocalStart] = useState(() => hhmmFromIso(startsAt));
  const [localHalf, setLocalHalf] = useState(displayHalf);
  const [localBreak, setLocalBreak] = useState(breakMins);
  const containerRef = useRef<HTMLDivElement>(null);
  // Snapshot of the values when edit mode started, used to revert on Escape.
  const editOriginRef = useRef<{ starts_at: string; first_half_mins: number; break_mins: number; second_half_mins: number; ends_at: string } | null>(null);

  // Sync local state when props change (e.g. from resize or drag)
  useEffect(() => {
    if (!editing) {
      setLocalStart(hhmmFromIso(startsAt));
      setLocalHalf(displayHalf);
      setLocalBreak(breakMins);
    }
  }, [startsAt, displayHalf, breakMins, editing]);

  // Live-update the card (height + label) while the user is typing in the
  // inline form, so they can see the new size before committing.
  function applyPreview(nextHalfRaw: number, nextBreakRaw: number, nextStartHHMM: string) {
    const half = Math.max(1, Math.round(Number.isFinite(nextHalfRaw) ? nextHalfRaw : 0));
    const br = Math.max(0, Math.round(Number.isFinite(nextBreakRaw) ? nextBreakRaw : 0));
    const [sh, sm] = nextStartHHMM.split(":").map(Number);
    const baseStart = new Date(startsAt);
    const newStart = new Date(baseStart);
    if (!Number.isNaN(sh) && !Number.isNaN(sm)) newStart.setHours(sh, sm, 0, 0);
    const totalMins = half + br + half;
    const newEnd = new Date(newStart.getTime() + totalMins * 60_000);
    const startIso = newStart.toISOString();
    const endIso = newEnd.toISOString();
    setMatches((prev) => prev.map((m) => m.id === matchId
      ? { ...m, starts_at: startIso, ends_at: endIso, first_half_mins: half, break_mins: br, second_half_mins: half }
      : m));
  }

  function beginEdit() {
    if (editing) return;
    editOriginRef.current = {
      starts_at: startsAt,
      ends_at: endsAt,
      first_half_mins: firstHalf,
      break_mins: breakMins,
      second_half_mins: secondHalf,
    };
    setEditing(true);
  }

  function cancelEdit() {
    const origin = editOriginRef.current;
    if (origin) {
      setMatches((prev) => prev.map((m) => m.id === matchId ? { ...m, ...origin } : m));
    }
    editOriginRef.current = null;
    setEditing(false);
  }

  async function save() {
    setEditing(false);
    // 123 R162 -- compare against the pre-edit snapshot, NOT live props. The
    // `applyPreview` calls on every keystroke update the parent's match state,
    // which feeds back into this component as fresh `firstHalf`/`breakMins`
    // props. Comparing against props made `durChanged` evaluate to false even
    // when the user genuinely changed the value, so no PATCH ever fired -- the
    // optimistic local update masked the missing persistence until the next
    // refetch, when the original DB value reappeared (the BA's "resets to
    // default" report).
    const origin = editOriginRef.current;
    editOriginRef.current = null;
    const half = Math.max(1, Math.round(localHalf));
    const b = Math.max(0, Math.round(localBreak));

    const [sh, sm] = localStart.split(":").map(Number);
    const snappedMin = Math.round(sm / 5) * 5;
    const newStart = new Date(startsAt);
    newStart.setHours(sh, snappedMin, 0, 0);
    const baseStartIso = origin?.starts_at ?? startsAt;
    const baseFirstHalf = origin?.first_half_mins ?? firstHalf;
    const baseBreak = origin?.break_mins ?? breakMins;
    const baseSecondHalf = origin?.second_half_mins ?? secondHalf;
    const startChanged = newStart.toISOString() !== baseStartIso;
    const durChanged = half !== baseFirstHalf || b !== baseBreak || half !== baseSecondHalf;
    if (!startChanged && !durChanged) return;

    const totalMins = half + b + half;
    const newEndsAt = new Date(newStart.getTime() + totalMins * 60_000).toISOString();
    const startIso = newStart.toISOString();

    setMatches((prev) => prev.map((m) => m.id === matchId
      ? { ...m, starts_at: startIso, first_half_mins: half, break_mins: b, second_half_mins: half, ends_at: newEndsAt }
      : m));

    const patch: { starts_at?: string; first_half_mins?: number; break_mins?: number; second_half_mins?: number } = {};
    if (startChanged) patch.starts_at = startIso;
    if (durChanged) { patch.first_half_mins = half; patch.break_mins = b; patch.second_half_mins = half; }
    notifySaving();
    try { await api.updateMatch(matchId, patch); notifySaved(); } catch { refreshMatches(); }
  }

  // Save when focus leaves the entire container
  function handleBlur(e: React.FocusEvent) {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
      save();
    }
  }

  if (editing) {
    return (
      <div ref={containerRef} className="mt-0.5 flex items-center gap-1 text-xs" onClick={(e) => e.stopPropagation()} onBlur={handleBlur}>
        <input type="time" value={localStart} step={300} title="Start time (5-min increments)"
          className="w-20 rounded border border-input bg-background px-1 py-0.5 text-center text-xs outline-none ring-1 ring-primary"
          onChange={(e) => {
            setLocalStart(e.target.value);
            applyPreview(localHalf, localBreak, e.target.value);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancelEdit(); }}
          autoFocus
        />
        <span className="text-muted-foreground">·</span>
        <input type="number" value={localHalf} min={1} max={60} step={1} title="Half length (min) -- both halves equal"
          className="w-10 rounded border border-input bg-background px-1 py-0.5 text-center text-xs outline-none ring-1 ring-primary"
          onChange={(e) => {
            const v = Number(e.target.value);
            setLocalHalf(v);
            applyPreview(v, localBreak, localStart);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancelEdit(); }}
        />
        <span className="text-muted-foreground">× 2 +</span>
        <input type="number" value={localBreak} min={0} max={30} step={1} title="Break (min)"
          className="w-10 rounded border border-input bg-background px-1 py-0.5 text-center text-xs outline-none ring-1 ring-primary"
          onChange={(e) => {
            const v = Number(e.target.value);
            setLocalBreak(v);
            applyPreview(localHalf, v, localStart);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancelEdit(); }}
        />
        <span className="text-xs text-muted-foreground">min</span>
      </div>
    );
  }

  return (
    <div
      onClick={canEdit ? (e) => { e.stopPropagation(); beginEdit(); } : undefined}
      title={canEdit ? "Click to edit game times" : "Game times"}
      className={`mt-0.5 text-[10px] ${isResizing ? "font-semibold text-primary" : "text-muted-foreground"} ${canEdit ? "cursor-pointer hover:text-foreground" : ""}`}>
      {fmtTime(startsAt)} - {fmtTime(endsAt)} · {displayHalf}'×2 + {breakMins}'
    </div>
  );
}

// ─── Referee Inline Edit ────────────────────────────────────────

function RefereeInline({ matchId, value, canEdit, setMatches, refreshMatches }: {
  matchId: string;
  value: string | null;
  canEdit: boolean;
  setMatches: Dispatch<SetStateAction<MatchPublic[]>>;
  refreshMatches: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);

  async function save(newValue: string) {
    const referee = newValue.trim() || null;
    setEditing(false);
    setMatches((prev) => prev.map((m) => m.id === matchId ? { ...m, referee } : m));
    notifySaving();
    try { await api.updateMatch(matchId, { referee }); notifySaved(); } catch { refreshMatches(); }
  }

  if (editing) {
    return (
      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
        <Flag className="h-3 w-3 shrink-0" />
        <input defaultValue={value ?? ""} autoFocus placeholder="Referee name" title="Referee name" data-field="referee"
          className="w-24 rounded border border-input bg-background px-1.5 py-0.5 text-[11px] outline-none ring-1 ring-primary"
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => save(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            if (e.key === "Escape") setEditing(false);
          }}
        />
      </div>
    );
  }

  if (value) {
    return (
      <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground"
        onClick={canEdit ? (e) => { e.stopPropagation(); setEditing(true); } : undefined}
        title={canEdit ? "Click to edit referee" : "Referee"}>
        <Flag className="h-3 w-3 shrink-0" strokeWidth={2.5} /> {value}
      </div>
    );
  }

  if (canEdit) {
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-50" title="Add referee">
        <Flag className="h-3 w-3 shrink-0" /> <span className="italic">ref</span>
      </button>
    );
  }

  return null;
}
